import {
  type ChangeEvent,
  type MouseEvent as ReactMouseEvent,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { formatTagLabel } from '@/shared/utils/tagLabel';
import { highlightMatch } from '../utils/highlightMatch';
import { useTagCombobox, type ComboboxOption } from './useTagCombobox';
import styles from './TagCombobox.module.css';

interface TagComboboxProps {
  draft: string;
  onDraftChange: (value: string) => void;
  /** Catalog values (canonical SCREAMING_SNAKE_CASE) to surface as suggestions. */
  suggestions: string[];
  /**
   * Commits a value to the parent. The parent's `commit` re-runs normalization
   * and dedup so the combobox doesn't need to know either rule.
   */
  onCommit: (text: string) => void;
  /**
   * When non-null, a "Create '<this>'" row is appended to the popover. The
   * parent decides when to show it (typically: typed value normalizes to a
   * canonical that's neither in the catalog nor already selected).
   */
  createLabel: string | null;
  /** Backspace on empty input — removes the last user-added tag. */
  onRemoveLast: () => void;
  /** Second Escape clears the typed text. */
  onClearDraft: () => void;
  disabled?: boolean;
}

export default function TagCombobox({
  draft,
  onDraftChange,
  suggestions,
  onCommit,
  createLabel,
  onRemoveLast,
  onClearDraft,
  disabled = false,
}: TagComboboxProps) {
  const wrapRef = useRef<HTMLDivElement>(null);

  const options: ComboboxOption[] = useMemo(() => {
    const opts: ComboboxOption[] = suggestions.map((value) => ({
      kind: 'suggestion',
      value,
      displayText: formatTagLabel(value),
    }));
    if (createLabel) {
      opts.push({
        kind: 'create',
        value: draft,
        displayText: createLabel,
      });
    }
    return opts;
  }, [createLabel, draft, suggestions]);

  const {
    open,
    setOpen,
    openPopover,
    close,
    activeIndex,
    setActiveIndex,
    listboxId,
    optionId,
    handleKeyDown,
    selectOption,
  } = useTagCombobox({
    draft,
    options,
    disabled,
    onCommit,
    onRemoveLast,
    onClearDraft,
  });

  const showPopover = !disabled && open && options.length > 0;

  // Click-outside closes the popover.
  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        close();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [close, open]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onDraftChange(event.target.value);
    setOpen(true);
    setActiveIndex(-1);
  };

  const handleOptionMouseDown = (event: ReactMouseEvent) => {
    // Prevent the input from blurring when the user clicks an option.
    event.preventDefault();
  };

  const trimmedDraft = draft.trim();

  return (
    <div ref={wrapRef} className={styles.wrap}>
      <div
        className={`${styles.inputRow} ${
          disabled ? styles.inputRowDisabled : ''
        }`}
      >
        <PlusOutlined aria-hidden className={styles.leadIcon} />
        <input
          type="text"
          role="combobox"
          aria-expanded={showPopover}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            showPopover && activeIndex >= 0 ? optionId(activeIndex) : undefined
          }
          className={styles.input}
          data-testid="tag-input"
          placeholder="Add a tag"
          value={draft}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={openPopover}
          disabled={disabled}
          autoComplete="off"
        />
        {trimmedDraft.length > 0 && (
          <kbd className={styles.kbdHint} aria-hidden>
            ↵
          </kbd>
        )}
      </div>

      {showPopover && (
        <div
          id={listboxId}
          role="listbox"
          className={styles.popover}
          data-testid="tag-popover"
        >
          {suggestions.length > 0 && (
            <div className={styles.sectionHeader}>Suggestions</div>
          )}
          {options.map((opt, index) => {
            const isActive = index === activeIndex;
            const id = optionId(index);

            if (opt.kind === 'create') {
              return (
                // Combobox pattern: focus stays on input, arrow keys drive
                // selection via aria-activedescendant — options are not in
                // the tab order and don't need their own key handlers.
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                <div
                  key="__create-row"
                  id={id}
                  role="option"
                  tabIndex={-1}
                  aria-selected={isActive}
                  className={`${styles.option} ${styles.createRow} ${
                    isActive ? styles.optionActive : ''
                  }`}
                  data-testid="tag-create-row"
                  onMouseDown={handleOptionMouseDown}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => selectOption(index)}
                >
                  <PlusOutlined aria-hidden className={styles.createIcon} />
                  <span>
                    Create{' '}
                    <span className={styles.createValue}>
                      &ldquo;{opt.displayText}&rdquo;
                    </span>
                  </span>
                </div>
              );
            }

            const segments = highlightMatch(opt.displayText, trimmedDraft);
            return (
              // Combobox pattern: focus stays on input, arrow keys drive
              // selection via aria-activedescendant — options are not in
              // the tab order and don't need their own key handlers.
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events
              <div
                key={opt.value}
                id={id}
                role="option"
                tabIndex={-1}
                aria-selected={isActive}
                className={`${styles.option} ${
                  isActive ? styles.optionActive : ''
                }`}
                data-testid={`tag-suggestion-${opt.value}`}
                onMouseDown={handleOptionMouseDown}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => selectOption(index)}
              >
                {segments ? (
                  <span>
                    {segments.before}
                    <mark className={styles.match}>{segments.match}</mark>
                    {segments.after}
                  </span>
                ) : (
                  opt.displayText
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
